import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu, Coins, Settings as SettingsIcon, LogOut } from "lucide-react";
import { trackNavigation } from "@/lib/analytics";

interface NavLink {
  to: string;
  label: string;
  onClick: () => void;
}

interface MobileNavSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { id: string } | null;
  navLinks: NavLink[];
  balance: number;
  creditsLoading: boolean;
  onSignOut: () => void;
}

export function MobileNavSheet({
  open,
  onOpenChange,
  user,
  navLinks,
  balance,
  creditsLoading,
  onSignOut,
}: MobileNavSheetProps) {
  const location = useLocation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          // h-11 (44px) on mobile to meet WCAG 2.5.5; shadcn's icon
          // default is h-10 (40px). Override only here, not globally,
          // so other icon-button uses (admin tables, dense toolbars)
          // keep their compact size.
          // xl:hidden must stay in step with Header.tsx's `hidden xl:flex` nav.
          // These two are one decision: if they ever drift, some band of widths
          // shows both navigations or neither. Moved from lg together with it,
          // because the header measured 1074px of content against lg's 1024px
          // and clipped the right-hand cluster in between.
          className="xl:hidden h-11 w-11"
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <div className="flex flex-col gap-4 mt-8">
          {user && (
            <Link
              to="/buy-coins"
              onClick={() => onOpenChange(false)}
              className="flex items-center gap-2 px-2 py-2 hover:bg-muted/50 rounded-md sm:hidden"
            >
              <Coins className="h-4 w-4 text-primary" />
              <span className="font-semibold">{creditsLoading ? '...' : balance}</span>
              <span className="text-sm text-muted-foreground">coins</span>
            </Link>
          )}
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={(event) => {
                if (link.to === "/generate" && location.pathname === "/generate") {
                  event.preventDefault();
                  trackNavigation('generate');
                  window.dispatchEvent(new CustomEvent("spotivibly:generate-reset-request"));
                  onOpenChange(false);
                  return;
                }
                link.onClick();
                onOpenChange(false);
              }}
              className="text-base font-medium uppercase tracking-[0.15em] text-foreground/80 hover:text-foreground transition-colors px-2 py-3 hover:bg-muted/50 rounded-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {link.label}
            </Link>
          ))}
          {user && (
            <>
              <Separator />
              <Link
                to="/account"
                onClick={() => onOpenChange(false)}
                className="text-lg font-medium text-foreground/80 hover:text-foreground transition-colors px-2 py-3 hover:bg-muted/50 rounded-md flex items-center gap-2"
              >
                <SettingsIcon className="h-5 w-5" />
                Account
              </Link>
              <button
                onClick={() => {
                  onSignOut();
                  onOpenChange(false);
                }}
                className="text-lg font-medium text-destructive hover:text-destructive/80 transition-colors px-2 py-3 hover:bg-muted/50 rounded-md flex items-center gap-2 text-left"
              >
                <LogOut className="h-5 w-5" />
                Sign Out
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
